import React, { useRef, useState } from 'react';
import PlaceInput from './PlaceInput';
import PhoneInput from 'react-phone-input-2';
import MapContainer from "../VehicleType/MapContainer";
import { motion } from 'framer-motion';
import { MapPin, Hospital, Navigation, Phone, Star, Clock } from 'lucide-react';
import styled from 'styled-components';
import { Header, Body, FormGroup, Label, Error, ButtonGroup, Button } from '../Ambulance/StyledComponents';
import { geocodeByAddress, getLatLng } from "react-google-places-autocomplete";
import { Modal } from 'react-bootstrap';

// Styled Components (unchanged)
const Container = styled(motion.div)`
  max-width: 100%;
  width: ${() => window.innerWidth > 1024 ? '1400px' : '99%'};
  min-height: 5vh;
  margin: 0 auto;
  padding: 1rem;
`;

const Grid = styled(motion.div)`
  display: grid;
  grid-template-columns: ${() => window.innerWidth > 1024 ? '1fr 1.5fr' : '1fr'};
  gap: 2rem;
`;

const FormSection = styled(motion.div)`
  width: 100%;
  max-width: 100%;
  background: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const MapSection = styled(motion.div)`
  width: 100%;
  max-width: 100%;
  height: ${() => window.innerWidth > 1024 ? '700px' : '400px'};
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const BookingLayout = ({
  startPoint,
  startPointError,
  destiny,
  destinyError,
  phone,
  phoneError,
  showLoading,
  handleSubmit,
  submitLabel,
  extraFields,
  hidePaymentMethods,
  handleStartPointChange,
  handleDestinyChange,
  handlePhoneChange,
  setStartPoint,
  setStartPointError,
  setDestiny,
  setDestinyError,
  mapKey,
  setPaymentMethod,
  paymentMethod
}) => {
  const [startCoords, setStartCoords] = useState(null);
  const [destinyCoords, setDestinyCoords] = useState(null);
  const [showHospitalModal, setShowHospitalModal] = useState(false);
  const [nearbyHospitals, setNearbyHospitals] = useState([]);
  const [loadingHospitals, setLoadingHospitals] = useState(false);
  const [selectedHospital, setSelectedHospital] = useState(null);

  // Payment method state
  // PaymentMethod moved up to parent 'Dispatch.js'
  const paymentOptions = [
    { value: 'Cash', label: 'Cash Payment', description: 'Pay with cash on delivery' },
    { value: 'Card', label: 'Card Payment', description: 'Pay with credit/debit card' },
    { value: 'Tele-birr', label: 'Tele-birr', description: 'Pay with Tele-birr mobile wallet' },
    { value: 'Corporate', label: 'Corporate Account', description: 'Use company account balance' }
  ];


  const selectHospital = (hospitalName, hospitalAddress) => {
    console.log('Selecting hospital:', hospitalName, hospitalAddress);

    // Create the proper format for GooglePlacesAutocomplete
    const hospitalPlaceObject = {
      label: `${hospitalName}, ${hospitalAddress}`,
      value: {
        description: `${hospitalName}, ${hospitalAddress}`,
        place_id: null, // We don't have place_id from our search, but it's not required for display
        structured_formatting: {
          main_text: hospitalName,
          secondary_text: hospitalAddress
        }
      }
    };

    // Set the selected hospital for display
    setSelectedHospital(hospitalPlaceObject);

    // Set the destiny with the proper object format
    setDestiny(hospitalPlaceObject);

    // Call parent's handleDestinyChange with the address string for form submission
    handleDestinyChange(hospitalPlaceObject.label);

    // Also update coordinates for the map
    handleDestiny(hospitalPlaceObject);

    setShowHospitalModal(false);
  };

  const removeSelectedHospital = () => {
    setSelectedHospital(null);
    setDestiny('');
    setDestinyCoords(null);
    handleDestinyChange('');
  };
  const fetchNearbyHospitals = async (location) => {
    setLoadingHospitals(true);

    if (!window.google || !window.google.maps) {
      console.error('Google Maps API not loaded');
      setLoadingHospitals(false);
      return;
    }

    try {
      const service = new window.google.maps.places.PlacesService(document.createElement('div'));

      const request = {
        location: new window.google.maps.LatLng(location.lat, location.lng),
        radius: 5000,
        type: 'hospital'
      };

      service.nearbySearch(request, (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
          const hospitals = results.map((place) => ({
            id: place.place_id,
            name: place.name,
            address: place.vicinity || place.formatted_address || 'Address not available',
            distance: 'Calculating...', // You can calculate distance using location
            rating: place.rating || 'N/A',
            phone: 'N/A', // Phone number would require an additional Places Details request
            specialties: ['General Medicine'] // Static placeholder
          }));
          setNearbyHospitals(hospitals);
        } else {
          console.error('Places service failed:', status);
          setNearbyHospitals([]);
        }
        setLoadingHospitals(false);
      });
    } catch (error) {
      console.error('Error fetching nearby hospitals:', error);
      setNearbyHospitals([]);
      setLoadingHospitals(false);
    }
  };

  const handleShowHospitals = () => {
    if (!startCoords) {
      setStartPointError('Please enter your starting point first to find nearest hospitals');
      return;
    }
    setStartPointError('');
    fetchNearbyHospitals(startCoords);
    setShowHospitalModal(true);
  };


  // Handle Start Point Change
  const handleStartPoint = async (value) => {
    try {
      const results = await geocodeByAddress(value.label); // Use value.label for geocoding
      const latLng = await getLatLng(results[0]);
      setStartCoords(latLng);
      handleStartPointChange(value.label); // Update parent state with address string
    } catch (err) {
      console.error("Geocoding error:", err);
      setStartPointError("Failed to locate address");
    }
  };

  // Handle Destination Change
  const handleDestiny = async (value) => {
    try {
      const results = await geocodeByAddress(value.label); // Use value.label for geocoding
      const latLng = await getLatLng(results[0]);
      setDestinyCoords(latLng);
      handleDestinyChange(value.label); // Update parent state with address string
    } catch (err) {
      console.error("Geocoding error:", err);
      setDestinyError("Failed to locate address");
    }
  };

  return (
    <Container>
      <Header>
        <MapPin />
        <h2>Your Booking Details</h2>
      </Header>
      <Grid>
        {/* Form Section */}
        <FormSection>
          <form onSubmit={handleSubmit} autoComplete="off" className="space-y-6">
            <div className="space-y-4">
              <FormGroup>
                <Label htmlFor="startPoint">Starting point </Label>
                <PlaceInput
                  key="startPointAutocomplete"
                  value={startPoint}
                  onChanges={handleStartPoint}
                  point={startPoint}
                  setPoint={setStartPoint}
                  error={startPointError}
                  setError={setStartPointError}
                  name="startingPoint"
                  id="startingPoint"
                />
                {startPointError && <Error>{startPointError}</Error>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="destiny">Where to</Label>
                <PlaceInput
                  key="destinyAutocomplete"
                  disabled={selectedHospital !== null}
                  value={destiny}
                  onChanges={handleDestiny}
                  point={destiny}
                  setPoint={setDestiny}
                  error={destinyError}
                  setError={setDestinyError}
                  name="destiny"
                  id="destiny"
                />
                {destinyError && <Error>{destinyError}</Error>}
                {selectedHospital && (
                  <div style={{
                    marginTop: '8px',
                    background: '#f0f9ff',
                    border: '1px solid #0ea5e9',
                    padding: '12px',
                    borderRadius: '6px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Hospital size={16} color="#0ea5e9" />
                      <span style={{ color: '#0369a1', fontWeight: '500', fontSize: '14px' }}>
                        Selected: {selectedHospital.label}
                      </span>
                    </div>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={removeSelectedHospital}
                      style={{
                        fontSize: '12px',
                        padding: '4px 8px',
                        borderRadius: '4px'
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                )}

                {/* Show Nearest Hospitals Button - Right after Where to field */}
                <div style={{ textAlign: 'center', margin: '8px 0' }}>
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={handleShowHospitals}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      color: 'white',
                      fontWeight: '500',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      width: '100%',
                      justifyContent: 'center'
                    }}
                  >
                    <Hospital size={16} />
                    Show Nearest Hospitals
                  </Button>
                </div>
              </FormGroup>

              <FormGroup>
                <Label>Phone Number</Label>
                <PhoneInput
                  id="phone"
                  country={"et"}
                  value={phone}
                  onChange={handlePhoneChange}
                  enableAreaCodes={true}
                  containerClass="phone-input-container"
                />
                {phoneError && <Error>{phoneError}</Error>}
              </FormGroup>

              {extraFields ? extraFields : null}

            </div>

            {/* Payment Method Selection */}
            {!hidePaymentMethods && (
              <FormGroup>
                <Label>Choose Payment Method</Label>
                <div className="payment-options" style={{
                  background: 'white',
                  borderRadius: '0.75rem',
                  padding: '1.5rem',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  marginBottom: '2rem'
                }}>
                  {paymentOptions.map(option => (
                    <label key={option.value} style={{
                      margin: '0 0 15px 0',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px',
                      borderRadius: '8px',
                      border: paymentMethod === option.value ? '2px solid #1e40af' : '1px solid #e2e8f0',
                      backgroundColor: paymentMethod === option.value ? '#f0f9ff' : '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value={option.value}
                        checked={paymentMethod === option.value}
                        onChange={() => setPaymentMethod(option.value)}
                        style={{ cursor: 'pointer' }}
                      />
                      <div style={{ marginLeft: '15px' }}>
                        <h4 style={{ margin: 0, color: '#1e293b', fontSize: '1rem' }}>{option.label}</h4>
                        <p style={{ margin: '4px 0 0', fontSize: '0.875rem', color: '#64748b' }}>{option.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </FormGroup>
            )}

            <Button
              type="submit"
              variant="primary"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={showLoading}
            >
              {showLoading ? "Booking..." : (submitLabel || "Book Now")}
            </Button>
          </form>
        </FormSection>

        {/* Map Section */}
        <MapSection>
          <MapContainer
            key={mapKey}
            startPoint={startCoords}
            destination={destinyCoords}
          />
        </MapSection>
      </Grid>

      {/* Hospital Selection Modal */}
      <Modal
        show={showHospitalModal}
        onHide={() => setShowHospitalModal(false)}
        size="lg"
        centered
        backdrop="static"
        keyboard={true}
        style={{ zIndex: 9999 }}
      >
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <Modal.Title style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Hospital size={24} />
            Nearest Hospitals from {startPoint}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '0' }}>
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {loadingHospitals ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <div style={{ marginBottom: '16px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #f3f4f6',
                    borderTop: '4px solid #667eea',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto'
                  }}></div>
                </div>
                <p style={{ color: '#6b7280', margin: '0' }}>Finding nearby hospitals...</p>
              </div>
            ) : nearbyHospitals.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <Hospital size={48} color="#9ca3af" style={{ marginBottom: '16px' }} />
                <p style={{ color: '#6b7280', margin: '0' }}>No hospitals found nearby</p>
              </div>
            ) : (
              nearbyHospitals.map((hospital) => (
                <div
                  key={hospital.id}
                  onClick={() => selectHospital(hospital.name, hospital.address)}
                  style={{
                    padding: '16px',
                    borderBottom: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    ':hover': {
                      backgroundColor: '#f3f4f6'
                    }
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <h6 style={{ margin: '0', color: '#1f2937', fontWeight: '600', fontSize: '16px' }}>
                      {hospital.name}
                    </h6>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Star size={16} fill="#fbbf24" color="#fbbf24" />
                      <span style={{ fontSize: '14px', color: '#6b7280' }}>{hospital.rating}</span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <Navigation size={14} color="#6b7280" />
                    <span style={{ fontSize: '14px', color: '#6b7280' }}>{hospital.address}</span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Clock size={14} color="#10b981" />
                      <span style={{ fontSize: '14px', color: '#10b981', fontWeight: '500' }}>{hospital.distance}</span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Phone size={14} color="#6b7280" />
                      <span style={{ fontSize: '14px', color: '#6b7280' }}>{hospital.phone}</span>
                    </div>
                  </div>

                  <div style={{ marginTop: '8px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {hospital.specialties.map((specialty, index) => (
                        <span
                          key={index}
                          style={{
                            background: '#dbeafe',
                            color: '#1e40af',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </Modal.Body>
        <Modal.Footer style={{ background: '#f9fafb', padding: '12px 24px' }}>
          <small style={{ color: '#6b7280', fontSize: '12px' }}>
            Click on any hospital to select it as your destination
          </small>
        </Modal.Footer>
      </Modal>
    </Container>
  );


};
export default BookingLayout;

